import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {Router} from '@angular/router';
import * as dayjs from 'dayjs';
import urlJoin from 'url-join';
import {BookTimeNowService} from '../../services/book-time-now.service';
import {UrlBuilderService} from '../../../../services/url-builder.service';
import {QueryParams} from '../../../../types/QueryParams';
import {TimeslotDetails} from '../../../../types/TimeslotDetails';
import {EventActionEnum, EventCategoryEnum} from '../../../../enums/GaTrackingEnums';
import {BookTimeNowTimeSlot} from '../../types/book-time-now-slot';
import {RouterLinkAndParams} from '../../types/router-link-and-params';
import {ToolsService} from '../../../../services/tools.service';
import {Subscription} from 'rxjs';
import {BookTimeNowThemeEnum} from '../../enums/book-time-now-theme.enum';
import {DataLayerParamsClickGA4} from '../../../../types/DataLayerParamsClickGA4';
import {BookingTypeEnum} from '../../../../enums/BookingEnums';
import {AppConfigService} from '../../../../services/app-config.service';
import {DLPageTypeEnum} from '../../../../enums/DataLayerEnums';
import {DataLayerListItemsEcommerceItem} from '../../../../types/DataLayerListItemsEcommerceItem';
import {DataLayerListItemsTag} from '../../../../types/DataLayerListItemsTag';
import {GtmEventEnum} from '../../../../enums/GoogleEnums';
import {GtmService} from '../../../../services/gtm.service';
import {SearchWidgetManagerService} from '../../../../services/search-widget/search-widget-manager.service';
import {SearchService} from '../../../../services/search/search.service';

export interface BookTimeNowTimeslotDetails {
  careProviderInfo: BookTimeNowTimeSlot;
  details: {
    label: string;
    routerLink: string[];
    queryParams: QueryParams;
  };
  eventParams: DataLayerParamsClickGA4;
}

@Component({
  selector: 'app-book-time-now-widget',
  templateUrl: './book-time-now-widget.component.html',
  styleUrls: ['./book-time-now-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookTimeNowWidgetComponent implements OnInit, OnDestroy {
  public mainCareProviderUnitTypeUrlPath = input<string>(null);
  public careProviderUnitId = input<number>(null);
  public careServiceTypeUrlPath = input<string>(null);
  public careServiceId = input<number>(null);
  public careServiceName = input<string>(null);
  public symptomUrlPath = input<string>(null);
  public symptomId = input<number>(null);
  public symptomName = input<string>(null);
  public useExpand = input<boolean>(true);
  public theme = input<BookTimeNowThemeEnum>(BookTimeNowThemeEnum.DEFAULT);
  public inferredCategoryTypeId = input<number>(null);
  public inferredCareProviderTypeAreaId = input<number>(null);
  public mainCareProviderTypeId = input<number>(null);
  public pageType = input<DLPageTypeEnum>(null);
  public negativeTop = input<string>('');
  public useSingleColLayout = input<boolean>(false);
  public expanded = signal<boolean>(null);
  public containerCssClass = computed<string>(() => {
    const classesArray: string[] = [];
    classesArray.push(this.theme());
    if (this.expanded()) {
      classesArray.push('expanded');
    }
    if (this.negativeTop() !== '') {
      classesArray.push(this.negativeTop());
    }
    if (this.useSingleColLayout()) {
      classesArray.push('single-col');
    }
    return classesArray.join(' ');
  });
  public headerText = computed<string>(() => {
    return this.pageType() === DLPageTypeEnum.CARE_SERVICE_TYPE_PAGE
      ? this.careServiceName()
        ? `Boka tid för ${this.careServiceName().toLowerCase()}`
        : 'Boka tid direkt'
      : 'Boka tid direkt';
  });
  public pushedItems = signal<DataLayerListItemsEcommerceItem[]>(null);
  public eventCategoryEnum = EventCategoryEnum;
  public eventActionEnum = EventActionEnum;
  public lat = input<number>(null);
  public lon = input<number>(null);
  public isGetLocationVisible = computed<boolean>(
    () => false,
    // (this.tools.isNil(this.lat()) || this.tools.isNil(this.lon())) && this.clinicsNearMeTriggered() !== true,
  );
  public isSeeMoreVisible = computed<boolean>(
    () =>
      !this.tools.isNil(this.latToUse()) &&
      !this.tools.isNil(this.lonToUse()) &&
      this.latToUse() != 0 &&
      this.lonToUse() != 0,
  );
  private clinicsNearMeTriggered = signal<boolean>(null);
  private searchService = inject(SearchService);
  private locationState = this.searchService.userLocationState;
  private userLat = computed<number>(() => (this.locationState()?.locationSearch ? this.locationState()?.lat : null));
  public latToUse = computed(() => {
    const stockholmLat: number = 59.3293;
    return this.clinicsNearMeTriggered() === true && this.userLat() && this.userLat() !== 0
      ? this.userLat()
      : this.lat() && this.lat() !== 0
        ? this.lat()
        : stockholmLat;
  });
  private userLon = computed<number>(() => (this.locationState()?.locationSearch ? this.locationState()?.lon : null));
  public lonToUse = computed(() => {
    const stockholmLon: number = 18.0686;
    return this.clinicsNearMeTriggered() === true && this.userLon() && this.userLon() !== 0
      ? this.userLon()
      : this.lon() && this.lon() !== 0
        ? this.lon()
        : stockholmLon;
  });
  private subscriptions: Subscription[] = [];
  private bookTimeNowService = inject(BookTimeNowService);
  private careproviderTimeslots = this.bookTimeNowService.careproviderTimeslots;
  private careservicetypeTimeslots = this.bookTimeNowService.careservicetypeTimeslots;
  private symptomTimeslots = this.bookTimeNowService.symptomTimeslots;
  public timeslotDetails = computed(() => {
    const careproviderSlots = this.careproviderTimeslots() ?? [];
    const careservicetypeSlots = this.careservicetypeTimeslots() ?? [];
    const symptomSlots = this.symptomTimeslots() ?? [];

    return this.getDetails([...careproviderSlots, ...careservicetypeSlots, ...symptomSlots]);
  });

  constructor(
    private urlBuilderService: UrlBuilderService,
    private tools: ToolsService,
    private router: Router,
    private appConfigService: AppConfigService,
    private gtmService: GtmService,
    private searchWidgetManager: SearchWidgetManagerService,
  ) {
    effect(
      () => {
        if (this.timeslotDetails()) {
          this.pushViewItemListEventToDataLayer(this.timeslotDetails());
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (!this.tools.isNil(this.careProviderUnitId())) {
          this.bookTimeNowService.reloadCareProviderTimeslots(this.careProviderUnitId());
        }
        if (!this.tools.isNil(this.careServiceId())) {
          this.bookTimeNowService.reloadCareServiceTimeslots(this.careServiceId(), this.latToUse(), this.lonToUse());
        }
        if (!this.tools.isNil(this.symptomId())) {
          this.bookTimeNowService.reloadSymptomsTimeslots(this.symptomId(), this.latToUse(), this.lonToUse());
        }
      },
      {allowSignalWrites: true},
    );
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy() {
    this.unsubscribeAll();
  }

  public expand(): void {
    this.expanded.set(true);
  }

  public onSeeMoreClick() {
    const urlParams: RouterLinkAndParams = this.bookTimeNowService.getSeeMoreUrlParams(
      this.mainCareProviderUnitTypeUrlPath(),
      this.careServiceTypeUrlPath(),
      this.symptomUrlPath(),
      this.latToUse(),
      this.lonToUse(),
    );
    this.router.navigate(urlParams.routerLink, {
      queryParams: urlParams.queryParams,
    });
  }

  public getUserLocation(): void {
    this.clinicsNearMeTriggered.set(true);
    this.searchWidgetManager.getUserLocation(null, false, false, false);
  }

  protected navigateToBookingUrl(routerLink: string[], queryParams: QueryParams): void {
    this.router.navigate(routerLink, {queryParams: queryParams});
  }

  private pushViewItemListEventToDataLayer(timeslots: BookTimeNowTimeslotDetails[]): void {
    const items: DataLayerListItemsEcommerceItem[] = [];
    timeslots.forEach((item) => {
      const ecommerceItem: DataLayerListItemsEcommerceItem = {
        item_id: item.careProviderInfo.itemId,
        cpru_id: item.careProviderInfo.careProviderUnitId,
        careservice_id: item.careProviderInfo.careServiceId,
        Pagetype: this.pageType(),
        careservice_type: item.careProviderInfo.careServiceTypeId,
        mainCareProviderTypeId: item.careProviderInfo.mainCareProviderUnitTypeId,
      };
      items.push(ecommerceItem);
    });
    if (items.length > 0 && !this.tools.isEqual(items, this.pushedItems())) {
      this.pushedItems.set(this.tools.clone(items));
      const tag: DataLayerListItemsTag = {
        event: GtmEventEnum.VIEW_ITEM_LIST,
        ecommerce: {
          items: items,
        },
      };
      this.gtmService.pushTag(tag, true);
    }
  }

  private genTimeslotDetails(careProviderUnitId: number, careServiceId: number, timeslot: string): TimeslotDetails {
    const day = dayjs(timeslot);
    let label = day.format('ddd D/M');
    label = label[0].toUpperCase() + label.slice(1);
    const timeslotRouterParam = day.unix().toString();

    if (day.date() === dayjs().date()) {
      label = 'Idag';
    }

    if (day.date() === dayjs().add(1, 'day').date()) {
      label = 'Imorgon';
    }

    const referral: string = urlJoin(this.appConfigService.appConfig.baseUrl, this.router.url);
    return {
      label: `${label} kl ${day.format('HH.mm')}`,
      routerLink: this.urlBuilderService.CreateInternalBookingRouterLink(
        careProviderUnitId,
        careServiceId,
        timeslotRouterParam,
      ),
      queryParams: this.urlBuilderService.CreateInternalBookingQueryParams(referral),
    };
  }

  private getDetails(timeslots: BookTimeNowTimeSlot[]): BookTimeNowTimeslotDetails[] {
    return timeslots?.map((t) => ({
      careProviderInfo: t,
      details: this.genTimeslotDetails(t.careProviderUnitId, t.careServiceId, t.timeslot),
      eventParams: this.getEventParams(t),
    }));
  }

  private init(): void {
    this.unsubscribeAll();
    this.expanded.set(this.useExpand() !== true);
  }

  private getEventParams(timeslot: BookTimeNowTimeSlot): DataLayerParamsClickGA4 {
    return {
      callToAction: `BookTimeNow widget - timeslot click`,
      bookingType: BookingTypeEnum.VARDEN,
      pageType: this.pageType(),
      cpruId: timeslot.careProviderUnitId,
      careServiceId: timeslot.careServiceId,
      careServiceTypeId: timeslot.careServiceTypeId,
      inferredCategoryTypeId: this.inferredCategoryTypeId()
        ? this.inferredCategoryTypeId()
        : timeslot.mainCareProviderUnitTypeId,
      inferredCareProviderTypeAreaId: this.inferredCareProviderTypeAreaId()
        ? this.inferredCareProviderTypeAreaId()
        : timeslot.mainCareProviderUnitTypeAreaId,
      mainCareProviderTypeId: timeslot.mainCareProviderUnitTypeId
        ? timeslot.mainCareProviderUnitTypeId
        : this.mainCareProviderTypeId(),
    };
  }

  private unsubscribeAll(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }
}
